import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import SalesSummaries from './pages/Reports/Sales/SalesSummaries';
import PaymentReports from './pages/Reports/Payment';
import InstantReports from './pages/Reports/Instant';
import DayTimesReports from './pages/Reports/DayTimes';
import StaffReports from './pages/Reports/Staff';
import UnpaidReports from './pages/Reports/Unpaid';
import GiftReports from './pages/Reports/Gift';
import TopProductsReports from './pages/Reports/TopProducts';
import TopWaitersReports from './pages/Reports/TopWaiters';
import AverageTableConsumptionReports from './pages/Reports/AverageTableConsumption';
import ProductCostSalesRatioReports from './pages/Reports/ProductCostSalesRatio';
import CriticalStockReports from './pages/Reports/CriticalStock';
import MainLayout from './components/Layout/MainLayout';
import './index.css';

// Ant Design theme configuration
const theme = {
  token: {
    colorPrimary: '#ff2c3c',
    colorLink: '#ff2c3c',
    colorInfo: '#ff2c3c',
    borderRadius: 6,
  },
  components: {
    Button: {
      colorPrimary: '#ff2c3c',
      colorPrimaryHover: '#ff5c6c',
      colorPrimaryActive: '#cc2230',
      algorithm: true,
    },
    Menu: {
      darkItemSelectedBg: '#ff2c3c',
      darkItemHoverBg: '#ff2c3c90',
    },
    Radio: {
      colorPrimary: '#ff2c3c',
      algorithm: true,
    },
  },
};

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const hasToken = localStorage.getItem('token');
  const hasUser = localStorage.getItem('userDetails');
  const isAuthenticated = !!hasToken && !!hasUser;

  useEffect(() => {
    if (isAuthenticated) {
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 3000);
      return () => clearTimeout(timer);
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white">
        <div className="loader-food w-[300px] h-[225px]"></div>
      </div>
    );
  }

  return <MainLayout>{children}</MainLayout>;
};

function App() {
  return (
    <ConfigProvider theme={theme}>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/sales-summaries"
            element={
              <ProtectedRoute>
                <SalesSummaries />
              </ProtectedRoute>
            }
          />

          <Route
            path="/top-products-reports"
            element={
              <ProtectedRoute>
                <TopProductsReports />
              </ProtectedRoute>
            }
          />

          <Route
            path="/top-waiters-reports"
            element={
              <ProtectedRoute>
                <TopWaitersReports />
              </ProtectedRoute>
            }
          />

          <Route
            path="/average-table-consumption-reports"
            element={
              <ProtectedRoute>
                <AverageTableConsumptionReports />
              </ProtectedRoute>
            }
          />

          <Route
            path="/product-cost-sales-ratio-reports"
            element={
              <ProtectedRoute>
                <ProductCostSalesRatioReports />
              </ProtectedRoute>
            }
          />

          <Route
            path="/critical-stock-reports"
            element={
              <ProtectedRoute>
                <CriticalStockReports />
              </ProtectedRoute>
            }
          />

          <Route
            path="/payment-reports"
            element={
              <ProtectedRoute>
                <PaymentReports />
              </ProtectedRoute>
            }
          />

          <Route
            path="/instant-reports"
            element={
              <ProtectedRoute>
                <InstantReports />
              </ProtectedRoute>
            }
          />

          <Route
            path="/day-times-reports"
            element={
              <ProtectedRoute>
                <DayTimesReports />
              </ProtectedRoute>
            }
          />

          <Route
            path="/staff-reports"
            element={
              <ProtectedRoute>
                <StaffReports />
              </ProtectedRoute>
            }
          />

          <Route
            path="/unpaid-reports"
            element={
              <ProtectedRoute>
                <UnpaidReports />
              </ProtectedRoute>
            }
          />

          <Route
            path="/gift-reports"
            element={
              <ProtectedRoute>
                <GiftReports />
              </ProtectedRoute>
            }
          />

          {/* Default Routes */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>
    </ConfigProvider>
  );
}

export default App;